import type * as React from 'react';
import { cn } from '../../lib/cn.ts';
import { CHAT_DRAWER_WIDTH, CHAT_STRIP_WIDTH } from '../chat/chatDrawerWidth.ts';

/**
 * What the chat drawer is doing, as far as the layout is concerned: standing
 * beside the page, collapsed to its strip, or out of the way altogether — full
 * screen, where there is no page left to make room for.
 */
export type AppShellChat = 'open' | 'collapsed' | 'none';

/**
 * How wide the content column is allowed to run: a reading column, or the
 * console width a page of tables and dashboards needs.
 */
export type AppShellContentWidth = 'reading' | 'wide';

/* Only a capped column has room left over to be centred in, so `mx-auto` rides along with the cap. */
const contentWidthClass: Record<AppShellContentWidth, string> = {
  reading: 'mx-auto max-w-240',
  wide: 'mx-auto max-w-console',
};

export interface AppShellProps extends Omit<React.ComponentProps<'div'>, 'style'> {
  /* The desktop icon rail. Hidden by the rail itself below the mobile breakpoint. */
  rail?: React.ReactNode;
  /*
   * A second navigation column between the rail and the page — the staff
   * console's list of the pages in the section you are standing in. A track of
   * its own rather than something the page draws, so the page is still a
   * centred column and not a flex row that has to cap itself.
   */
  sidebar?: React.ReactNode;
  /* The mobile topbar + slide-in nav. Hidden by themselves above the breakpoint. */
  mobileNav?: React.ReactNode;
  /* A `MobileTabBar`. Its own slot: only the shell can pad the page so the bar never covers its end. */
  tabBar?: React.ReactNode;
  /*
   * A full-width strip above everything else — the impersonation banner, and
   * nothing else so far. It is a slot rather than something the caller renders
   * beside the shell because this is a grid: a sibling above it would leave the
   * rail column starting at the top of the page, behind the strip.
   */
  banner?: React.ReactNode;
  /*
   * What the chat drawer is doing. The drawer positions itself; all the shell
   * does is reserve the track it stands in.
   */
  chat?: AppShellChat;
  /*
   * How wide the open drawer is drawn — `chatDrawerFit`'s answer, not the width
   * the user asked for. The track is exactly that, so the two cannot disagree.
   */
  chatWidth?: number;
  /*
   * Whether the drawer stands beside the page rather than over it. Decided by
   * `chatDrawerFit`, because it depends on how far this page's content may be
   * squeezed and only the page knows that.
   */
  chatPushes?: boolean;
  /*
   * Which column this page is. `reading` — the default, and every page of the
   * customer app — is the 960px column prose is set in. `wide` is for the staff
   * console, where the page is a table or a dashboard and a reading column
   * would throw away half the monitor. Both are centred, and only the cap moves
   * between them.
   */
  contentWidth?: AppShellContentWidth;
}

/*
 * The frame every page sits in: a rail, an optional second navigation column,
 * and the content beside them.
 *
 * The column is capped so it measures the same whether or not a chat drawer is
 * open beside it — 960px to read in, or the console width where the page is a
 * table. Which of the two is an app's decision, taken once where the shell is
 * mounted; never re-cap it per page, the consistency is the point. It is
 * centered in the track it is given, so a wide monitor splits the spare room
 * over both sides instead of piling it all up on the right — and a sidebar
 * takes a track rather than a slice of the page, so that stays true beside it.
 * Below 680px the grid collapses to a single block and the mobile topbar takes
 * over.
 *
 * The drawer's track is not a breakpoint and not this component's decision: it
 * is handed the width and whether that width stands beside the page, both from
 * `chatDrawerFit`, and lays out what it is told. Where the drawer does not
 * stand beside the page, no track is given and it lies over it.
 *
 * The collapsed strip always gets its 44px, fitting or not. That is what stops
 * the page jumping sideways every time the drawer is put away and taken out
 * again — the content column has to measure the same in both states.
 */
export function AppShell({
  rail,
  sidebar,
  mobileNav,
  tabBar,
  banner,
  chat = 'none',
  chatWidth = CHAT_DRAWER_WIDTH,
  chatPushes = false,
  contentWidth = 'reading',
  className,
  children,
  ...props
}: AppShellProps) {
  const pushes = chat === 'open' && chatPushes;

  const track = chat === 'collapsed' ? CHAT_STRIP_WIDTH : pushes ? chatWidth : 0;

  /*
   * The empty last column is always written, even at 0px: a grid going from two
   * tracks to three has nothing to interpolate, and the drawer would snap in
   * beside a page that jumps rather than slides.
   *
   * The rail track is the `--rail-w` token rather than the JS constant: under
   * the agenda breakpoint the rail narrows to icon width (DEV-36), and a CSS
   * property is the only way the track follows without a resize listener.
   *
   * The sidebar's track comes and goes with a breakpoint rather than with a
   * gesture, so unlike the drawer it has nothing to slide and is simply absent.
   */
  const columns = [
    rail ? 'var(--rail-w)' : '',
    sidebar ? 'var(--sidebar-w)' : '',
    '1fr',
    `${track}px`,
  ]
    .filter(Boolean)
    .join(' ');

  const shell = (
    <div
      data-slot='app-shell'
      data-chat={chat}
      data-chat-push={pushes}
      style={{ gridTemplateColumns: columns }}
      className={cn(
        'grid max-mobile:block',
        /*
         * The banner spends part of the viewport, so the shell budgets for the
         * remainder — otherwise every page is taller than the screen by
         * exactly the banner and the rail's foot falls off the bottom.
         */
        banner ? 'min-h-[calc(100dvh-var(--app-banner-h))]' : 'min-h-screen',
        /* The drawer track animates in and out; the track and its contents move as one thing. */
        'transition-[grid-template-columns] duration-200 ease-out',
        className,
      )}
      {...props}
    >
      {mobileNav}
      {rail}
      {sidebar}
      {/*
        pt-9 puts the page title on the same 36px line as the rail logo; the
        rail's own spacing lands its first icon at 130px, where page bodies start.
      */}
      <main
        data-content-width={contentWidth}
        className={cn(
          'relative w-full px-[clamp(20px,3.4vw,52px)] pt-9 pb-15 max-mobile:pt-6',
          /* The tab bar's 3.5rem and its safe area, over the usual 1.5rem of air. */
          tabBar && 'max-mobile:pb-[calc(5rem+env(safe-area-inset-bottom,0px))]',
          /* A column only for a page that fills it; every other page is laid out as before. */
          'has-data-fill:flex has-data-fill:flex-col',
          contentWidthClass[contentWidth],
        )}
      >
        {children}
      </main>
      {tabBar}
    </div>
  );

  if (!banner) {
    return shell;
  }

  /*
   * The banner stands outside the grid: a sticky element cannot outlive its
   * containing block, and a 48px grid row is no place to stay pinned from. Out
   * here it sticks to the viewport for as long as the page scrolls, and the
   * `--app-banner-h` it publishes is what the rail and the mobile topbar
   * subtract from their own height and offset.
   */
  return (
    <div data-slot='app-shell-frame' className='[--app-banner-h:3rem]'>
      <div className='sticky top-0 z-20 h-(--app-banner-h)'>{banner}</div>
      {shell}
    </div>
  );
}

export interface PageBodyProps extends Omit<React.ComponentProps<'div'>, 'style'> {
  /**
   * Whether this page has no title above the body. The gap below is the room a
   * title needs, so a page whose first element *is* its heading — a meeting,
   * whose hero bar carries the name — has to start at the top instead of
   * hanging half a screen below the rail's first icon.
   */
  flush?: boolean;
  /**
   * Whether this page is a single fixed-height surface — a transcript, a
   * board — rather than a document that scrolls. The body then takes the rest
   * of the viewport, keeping only a gutter's worth of the shell's bottom
   * padding, and scrolls inside itself.
   */
  fill?: boolean;
}

/*
 * Everything on a page below its title.
 *
 * The gap is the content half of the alignment grid: it drops the first body
 * element onto the same 130px line as the first rail icon. Detail pages spend
 * that space on a back-link, list pages leave it empty — either way the body
 * starts in the same place, so pages with and without a back-link align.
 */
export function PageBody({
  className,
  flush = false,
  fill = false,
  ...props
}: PageBodyProps) {
  return (
    <div
      data-slot='page-body'
      data-fill={fill ? '' : undefined}
      className={cn(
        /* A phone has no rail to line up with, so the body follows the title at ordinary spacing. */
        flush ? 'mt-0' : 'mt-13.5 max-mobile:mt-6',
        /*
         * A zero basis, or the body would be measured by its content first and
         * the page would grow to fit rather than the body to the page.
         */
        fill && 'min-h-0 flex-1 basis-0 -mb-10',
        className,
      )}
      {...props}
    />
  );
}
